import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { platformMeta } from '../promote/platformMeta';
import { SerializedSocialPost } from '../promote/types';
import { socialMessages as messages } from './socialMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import shared from '../promote/Promote.module.scss';
import styles from './Social.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SocialPostTilesProps {
  post: SerializedSocialPost;
}

const baseTiles = [
  { key: 'views', label: messages.views },
  { key: 'likes', label: messages.likes },
  { key: 'comments', label: messages.comments },
  { key: 'shares', label: messages.shares },
  { key: 'saves', label: messages.saves },
  { key: 'reach', label: messages.reach },
] as const;

/** Only X gives the link clicks: a seventh tile when the number is there (design x-connector e1). */
const linkClicksTile = { key: 'linkClicks', label: messages.linkClicks } as const;

/**
 * The six totals of a post, and "Link clicks" for an X post. A number that the platform does not
 * give is a dash, never 0, and a line below the tiles says why.
 */
const SocialPostTilesComponent: React.FC<SocialPostTilesProps> = ({ post }) => {
  const intl = useIntl();
  const missingHint = intl.formatMessage(messages.detailMissing, { platform: platformMeta[post.platform].name });
  const hasLinkClicks = post.linkClicks !== null;
  const tiles = useMemo(() => (hasLinkClicks ? [...baseTiles, linkClicksTile] : [...baseTiles]), [hasLinkClicks]);

  const cells = useMemo(
    () =>
      tiles.map(({ key, label }) => {
        const value = post[key];
        return (
          <div key={key} className={styles.tile} title={value === null ? missingHint : undefined}>
            <small>{intl.formatMessage(label)}</small>
            <span className={clsx(value === null && shared.dash)} data-testid={`social-post-tile-${key}`}>
              {value === null ? '—' : intl.formatNumber(value)}
            </span>
          </div>
        );
      }),
    [intl, missingHint, post, tiles]
  );
  const hasMissing = useMemo(() => tiles.some(({ key }) => post[key] === null), [post, tiles]);

  return (
    <>
      <div className={clsx(styles.tiles, hasLinkClicks && styles.tilesSeven)}>{cells}</div>
      {hasMissing ? (
        <div className={styles.detailNote}>
          <span className={shared.dash}>—</span>&nbsp; {missingHint}
        </div>
      ) : null}
    </>
  );
};

export const SocialPostTiles = TypedMemo(SocialPostTilesComponent);
